import { useCallback, useEffect, useRef, useState } from 'react';

import { useTimeout } from 'common/hooks/useTimeout';
import {
  sendAnalyticsInfo,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';
import { useAppDispatch } from 'features/app/hooks';

// Keep Cancel as a spinner on initial inbound H2D connect so agents do not
// cancel immediately when the call is received.
export const INBOUND_H2D_CANCEL_DELAY_MS = 10_000;

const toDurationSeconds = (startedAtMs: number) =>
  Number(((Date.now() - startedAtMs) / 1000).toFixed(2));

/**
 * Returns whether Cancel should stay non-clickable (spinner) for the
 * initial inbound H2D connecting window, plus a cancel-click reporter.
 *
 * Also emits:
 * - WARNING (`InboundH2DCancelAvailable`) when the delay elapses and Cancel
 *   becomes actionable while still not connected
 * - INFO (`InboundH2DHearingConnectedTime`) with DurationSeconds from
 *   connecting-UI start to hearing connect
 * - INFO (`InboundH2DCancelClickedTime`) with DurationSeconds from connecting-UI
 *   start to Cancel click after Cancel became available
 */
export const useInboundHearingCancelDelay = (
  enabled: boolean,
  isHearingConnected = false
) => {
  const dispatch = useAppDispatch();
  const [prevEnabled, setPrevEnabled] = useState(enabled);
  const [isDelayed, setIsDelayed] = useState(enabled);
  const connectingStartedAtRef = useRef<number | null>(null);
  const hasLoggedDelayWarningRef = useRef(false);
  const hasLoggedConnectRef = useRef(false);
  const hasLoggedCancelClickRef = useRef(false);

  // Reset the delay window when `enabled` flips (render-time state adjust).
  if (enabled !== prevEnabled) {
    setPrevEnabled(enabled);
    setIsDelayed(enabled);
  }

  useEffect(() => {
    if (enabled) {
      // Do not re-seed after connect logging cleared the start time while
      // enabled can still be true (hasConnected / loading lag).
      if (connectingStartedAtRef.current === null && !isHearingConnected) {
        connectingStartedAtRef.current = Date.now();
        hasLoggedDelayWarningRef.current = false;
        hasLoggedConnectRef.current = false;
        hasLoggedCancelClickRef.current = false;
      }
      return;
    }

    // Left connecting without a connect (cancel / disconnect).
    if (!isHearingConnected) {
      connectingStartedAtRef.current = null;
    }
  }, [enabled, isHearingConnected]);

  useTimeout(
    () => {
      setIsDelayed(false);

      if (
        hasLoggedDelayWarningRef.current ||
        connectingStartedAtRef.current === null ||
        isHearingConnected
      ) {
        return;
      }

      hasLoggedDelayWarningRef.current = true;
      dispatch(
        sendAnalyticsWarning({
          Method: 'InboundH2DCancelAvailable',
          Message:
            'Inbound H2D Cancel available after delay; hearing still not connected',
          DelaySeconds: INBOUND_H2D_CANCEL_DELAY_MS / 1000,
        })
      );
    },
    enabled && isDelayed ? INBOUND_H2D_CANCEL_DELAY_MS : null
  );

  useEffect(() => {
    if (
      !isHearingConnected ||
      connectingStartedAtRef.current === null ||
      hasLoggedConnectRef.current
    ) {
      return;
    }

    hasLoggedConnectRef.current = true;
    const durationSeconds = toDurationSeconds(connectingStartedAtRef.current);
    connectingStartedAtRef.current = null;

    dispatch(
      sendAnalyticsInfo({
        Method: 'InboundH2DHearingConnectedTime',
        Message: 'Inbound H2D hearing connected',
        DurationSeconds: durationSeconds,
      })
    );
  }, [isHearingConnected, dispatch]);

  const reportCancelClicked = useCallback(() => {
    if (
      !hasLoggedDelayWarningRef.current ||
      connectingStartedAtRef.current === null ||
      hasLoggedCancelClickRef.current
    ) {
      return;
    }

    hasLoggedCancelClickRef.current = true;
    const durationSeconds = toDurationSeconds(connectingStartedAtRef.current);
    connectingStartedAtRef.current = null;

    dispatch(
      sendAnalyticsInfo({
        Method: 'InboundH2DCancelClickedTime',
        Message:
          'Inbound H2D Cancel clicked after delay; measured from connecting start',
        DurationSeconds: durationSeconds,
      })
    );
  }, [dispatch]);

  return {
    isCancelDelayed: enabled && isDelayed,
    reportCancelClicked,
  };
};
